import { getAuthCookies, clearAuthCookies, refreshSession } from './actions';

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api/v1';

/* ======================================================
   AUTH HEADERS
====================================================== */
async function getAuthHeaders() {
  const { accessToken } = await getAuthCookies();

  const headers: Record<string, string> = {};

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return headers;
}

/* ======================================================
   REFRESH TOKEN
====================================================== */
type RefreshResult = 'ok' | 'invalid' | 'error';

async function refreshToken(): Promise<RefreshResult> {
  try {
    const { refreshToken } = await getAuthCookies();
    if (!refreshToken) return 'invalid';

    const res = await refreshSession(refreshToken, API_BASE_URL + '/auth');

    if (!res) return 'invalid';

    return 'ok';
  } catch {
    return 'error';
  }
}

/* ======================================================
   CORE REQUEST (WITH RETRY)
====================================================== */
async function request<T>(
  method: string,
  endpoint: string,
  body?: any,
): Promise<T> {
  const isFormData = body instanceof FormData;

  let headers = await getAuthHeaders();

  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  let response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers,
    credentials: 'include',
    body: isFormData ? body : body ? JSON.stringify(body) : undefined,
  });

  /* ===============================
     401 → TRY REFRESH ONCE
  =============================== */
  if (response.status === 401) {
    const refreshResult = await refreshToken();

    if (refreshResult === 'invalid') {
      await clearAuthCookies();
      document.dispatchEvent(new Event('auth:logout'));
      throw new Error('Session expired');
    }

    if (refreshResult === 'error') {
      throw new Error('Authentication temporarily unavailable');
    }

    // retry original request
    headers = await getAuthHeaders();

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers,
      credentials: 'include',
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
    });
  }

  return handleResponse<T>(response);
}

/* ======================================================
   RESPONSE HANDLER
====================================================== */
async function handleResponse<T>(response: Response): Promise<T> {
  const text = await response.text();

  let data: any;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }

  if (!response.ok) {
    // Hard auth failure
    if (response.status === 401 || response.status === 403) {
      document.dispatchEvent(new Event('auth:logout'));
    }

    const message = data?.error || data?.message || 'Server error';

    const error: any = new Error(message);
    error.status = response.status;
    error.response = { data };

    throw error;
  }

  return data as T;
}

export const apiClient = {
  get: <T,>(endpoint: string) => request<T>('GET', endpoint),

  post: <T,>(endpoint: string, body: any) => request<T>('POST', endpoint, body),

  put: <T,>(endpoint: string, body: any) => request<T>('PUT', endpoint, body),

  patch: <T,>(endpoint: string, body?: any) =>
    request<T>('PATCH', endpoint, body),

  delete: <T,>(endpoint: string) => request<T>('DELETE', endpoint),
};
